import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { Box, Typography } from '@mui/material';
import { LineChartPro } from '@mui/x-charts-pro/LineChartPro';
import type { ZoomData } from '@mui/x-charts-pro/models';
import type { HighlightItemData } from '@mui/x-charts/context';
import { useSvgRef, useYScale } from '@mui/x-charts/hooks';
import type { AxisItemIdentifier, ChartsAxisData } from '@mui/x-charts/models';
import type { VisibilityIdentifier } from '@mui/x-charts/plugins';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import { formatNumberToCurrency } from '@/utils/currency';
import { APP_MONTH_DAY_FORMAT, formatAppDate } from '@/utils/date';
import {
  type WealthTrendChart,
  type WealthTrendPointRef,
  isDenseTrend,
  minZoomSpan,
  nearestLine,
} from './wealthTrendChartData';

/** The x axis the zoom applies to. */
export const WEALTH_TREND_X_AXIS_ID = 'wealth-trend-days';

/** How long the pointer rests on a point before its popover opens, as legacy. */
const HOVER_OPEN_DELAY_MS = 500;

/** How far the pointer may move while pressed and still count as a click. */
const DRAG_THRESHOLD_PX = 4;

/** The axis never ticks closer than a day apart: the data is one point a day. */
const DAY_MS = 24 * 60 * 60 * 1000;

/** Room each date label gets, so the axis has no more ticks than fit a label. */
const TICK_LABEL_SPACING_PX = 90;

/** As many ticks as there is room for a label at each, once the width is known. */
function tickNumberFor(widthPx: number | null): number | undefined {
  if (!widthPx) return undefined;
  return Math.max(2, Math.floor(widthPx / TICK_LABEL_SPACING_PX));
}

/** How many days the zoom shows, from the axis's zoom percentages. */
function visibleDayCount(
  dayCount: number,
  zoomData: readonly ZoomData[],
): number {
  const zoom = zoomData.find(
    (entry) => entry.axisId === WEALTH_TREND_X_AXIS_ID,
  );
  if (!zoom) return dayCount;
  return Math.ceil(((dayCount - 1) * (zoom.end - zoom.start)) / 100) + 1;
}

/** Steps between ticks, in days: every day, then every few days, then weeks. */
const TICK_STEP_DAYS = [1, 2, 3, 7, 14] as const;

/**
 * The ticks to draw, always on a day boundary and a whole number of days apart,
 * so every tick has a label and no date repeats. It picks the smallest step
 * that fits the visible days into the room there is for labels. The library's
 * own ticks over a few days fell every few hours, so each date repeated, and
 * in its two-day mode it added an unlabelled tick at each month start. Past
 * fourteen-day steps, the library's monthly ticks are kept.
 */
function ticksFor(
  days: readonly Date[],
  zoomData: readonly ZoomData[],
  tickNumber: number | undefined,
): Date[] | undefined {
  if (tickNumber === undefined || days.length === 0) return undefined;
  const visible = visibleDayCount(days.length, zoomData);
  const step = TICK_STEP_DAYS.find((days) => visible / days <= tickNumber);
  if (step === undefined) return undefined;
  return days.filter((_day, index) => index % step === 0);
}

/**
 * The y axis range over the lines still shown, so hiding the larger line
 * rescales the axis to the one left, as legacy did. Undefined lets the library
 * pick its own range: when every line shows, or none does.
 */
function visibleRange(
  chart: WealthTrendChart,
  hiddenSeries: ReadonlySet<string>,
): { min: number; max: number } | undefined {
  if (hiddenSeries.size === 0) return undefined;
  const values = chart.lines
    .filter((line) => !hiddenSeries.has(seriesKey(line.id)))
    .flatMap((line) => line.values)
    .filter((value): value is number => value !== null);
  if (values.length === 0) return undefined;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = (max - min) * 0.05 || Math.abs(max) * 0.01 || 1;
  return { min: min - pad, max: max + pad };
}

const CHART_HEIGHT = 320;

/**
 * Room for the date labels. Once the chart has hydrated, MUI shortens each
 * label until it fits the axis height, measured in the app's font; at its
 * default height that left no room, so every label shortened to nothing.
 */
const X_AXIS_HEIGHT = 40;

/**
 * Ends the date axis on the first and last days with data. MUI's default rounds
 * a time axis out to its tick boundaries, so a range ending Oct 1 ran to Oct 4.
 */
const X_AXIS_DOMAIN_LIMIT = 'strict';

/** Maps a pointer's screen height to the line nearest it on a day. */
type LineLocator = (dayIndex: number, clientY: number) => number | null;

interface WealthTrendLineChartProps {
  chart: WealthTrendChart;
  zoomData: readonly ZoomData[];
  onZoomChange: (zoomData: ZoomData[]) => void;
  /** Off on mobile, where legacy shows a plain tooltip instead of the popover. */
  showPopover: boolean;
  onOpenPoint: (
    ref: WealthTrendPointRef,
    anchor: { top: number; left: number },
  ) => void;
  /** Legend below the chart rather than beside it, for narrow widths. */
  isCompact: boolean;
  /** The legend's title -- legacy's "Click item to hide". */
  legendHint: string;
  /** A touch screen, where a pinch zooms as well as a drag. */
  isTouch: boolean;
  /** A drag has begun: an open popover would sit over the area being dragged. */
  onDragStart: () => void;
  hideLegend?: boolean;
  showZoomSlider?: boolean;
  tooltipDateFormat?: string;
}

/** Room above the plot for the legend's title, so the two never overlap. */
const LEGEND_HINT_SPACE = 3;

function seriesKey(id: number): string {
  return `series-${String(id)}`;
}

function seriesIdFromKey(key: string | number): number | null {
  const match = /^series-(\d+)$/.exec(String(key));
  return match ? Number(match[1]) : null;
}

/** Axis labels in compact currency: 17.5M rather than $17,500,000.00. */
function formatAxisValue(value: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

interface LineLocatorBridgeProps {
  chart: WealthTrendChart;
  locator: MutableRefObject<LineLocator | null>;
}

/**
 * Renders nothing; lives inside the chart to lend its y scale and SVG to the
 * handlers outside it, so a pointer's height can be matched to a line when no
 * dot was hovered.
 */
function LineLocatorBridge({ chart, locator }: LineLocatorBridgeProps) {
  const yScale = useYScale();
  const svgRef = useSvgRef();

  useEffect(() => {
    locator.current = (dayIndex, clientY) => {
      const top = svgRef.current?.getBoundingClientRect().top ?? 0;
      return nearestLine(chart, dayIndex, clientY - top, (value) =>
        Number(yScale(value)),
      );
    };
    return () => {
      locator.current = null;
    };
  }, [chart, locator, svgRef, yScale]);

  return null;
}

/**
 * The managed and held-away lines on one time axis.
 *
 * Dragging across the plot zooms into those days (`brush`); there is no reset
 * control, as the ticket asks -- choosing a range again resets it. Clicking a
 * legend item hides or shows its line; legacy's "Click item to hide" sits as
 * its title, above it at the top right, or under it when the legend moves
 * below the chart.
 *
 * Short ranges draw a dot on every day, and a dot opens its popover on click
 * or after resting on it for half a second, as legacy did. Longer ranges draw
 * the lines alone -- a dot a day overlaps into a blur -- and show a dot only on
 * the hovered day, like legacy's chart; there the popover opens for the hovered
 * day on whichever line the pointer is nearest. Neither opens while the
 * pointer is held down, nor on the release that ends a drag, so a popover
 * never lands on the chart mid-zoom; a drag also closes a popover already
 * open, which would otherwise sit over the area being dragged.
 *
 * Ticks are at least a day apart, and no closer than a date label needs, so no
 * label repeats or is hidden for lack of room. A drag can zoom down to a
 * single day, even inside a range already zoomed.
 *
 * A line draws straight across a day it has no value for, as legacy's chart
 * did: there each series was its own list of dated points, so a missing day
 * was not there to break the line. The nulls exist only because both lines
 * share one axis here.
 */
export default function WealthTrendLineChart({
  chart,
  zoomData,
  onZoomChange,
  showPopover,
  onOpenPoint,
  isCompact,
  legendHint,
  isTouch,
  onDragStart,
  hideLegend = false,
  showZoomSlider = false,
  tooltipDateFormat = APP_MONTH_DAY_FORMAT,
}: WealthTrendLineChartProps) {
  const showLegendHint = !hideLegend && !isCompact;
  const pointer = useRef({ top: 0, left: 0 });
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const locator = useRef<LineLocator | null>(null);
  const press = useRef<{ left: number; top: number } | null>(null);
  const dragged = useRef(false);
  const dense = isDenseTrend(chart);
  const { ref: plotRef, widthPx } = useWidgetWidthIsBelow(true, 0);
  const tickNumber = tickNumberFor(widthPx);
  const [hiddenSeries, setHiddenSeries] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const yRange = visibleRange(chart, hiddenSeries);
  const handleHiddenItemsChange = useCallback(
    (hiddenItems: VisibilityIdentifier[]) => {
      setHiddenSeries(
        new Set(
          hiddenItems.flatMap((item) =>
            item.seriesId === undefined ? [] : [String(item.seriesId)],
          ),
        ),
      );
    },
    [],
  );
  const dayDates = useMemo(
    () => chart.days.map((day) => day.toDate()),
    [chart.days],
  );

  const clearHoverTimer = useCallback(() => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
  }, []);

  useEffect(() => clearHoverTimer, [clearHoverTimer]);

  useEffect(() => {
    const release = () => {
      press.current = null;
    };
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
    return () => {
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', release);
    };
  }, []);

  const openPoint = useCallback(
    (seriesId: number | null, dayIndex: number | undefined) => {
      if (seriesId === null || dayIndex === undefined) return;
      onOpenPoint({ seriesId, dayIndex }, pointer.current);
    },
    [onOpenPoint],
  );

  const openOnNearestLine = useCallback(
    (dayIndex: number) => {
      openPoint(
        locator.current?.(dayIndex, pointer.current.top) ?? null,
        dayIndex,
      );
    },
    [openPoint],
  );

  const scheduleOpen = useCallback(
    (open: () => void) => {
      clearHoverTimer();
      if (!showPopover || press.current) return;
      hoverTimer.current = setTimeout(open, HOVER_OPEN_DELAY_MS);
    },
    [clearHoverTimer, showPopover],
  );

  const handleHighlightChange = useCallback(
    (item: HighlightItemData | null) => {
      if (dense) return;
      if (!item) {
        clearHoverTimer();
        return;
      }
      scheduleOpen(() => {
        openPoint(seriesIdFromKey(item.seriesId), item.dataIndex);
      });
    },
    [clearHoverTimer, dense, openPoint, scheduleOpen],
  );

  const handleHighlightedAxisChange = useCallback(
    (items: AxisItemIdentifier[]) => {
      if (!dense) return;
      const item = items.find(
        (entry) => entry.axisId === WEALTH_TREND_X_AXIS_ID,
      );
      if (!item) {
        clearHoverTimer();
        return;
      }
      scheduleOpen(() => {
        openOnNearestLine(item.dataIndex);
      });
    },
    [clearHoverTimer, dense, openOnNearestLine, scheduleOpen],
  );

  const handleAxisClick = useCallback(
    (event: MouseEvent, data: ChartsAxisData | null) => {
      if (!dense || !showPopover || !data || dragged.current) return;
      clearHoverTimer();
      pointer.current = { top: event.clientY, left: event.clientX };
      openOnNearestLine(data.dataIndex);
    },
    [clearHoverTimer, dense, openOnNearestLine, showPopover],
  );

  return (
    <Box
      ref={plotRef}
      onPointerDownCapture={(event) => {
        clearHoverTimer();
        press.current = { left: event.clientX, top: event.clientY };
        dragged.current = false;
      }}
      onPointerMove={(event) => {
        pointer.current = { top: event.clientY, left: event.clientX };
        if (
          press.current &&
          Math.hypot(
            event.clientX - press.current.left,
            event.clientY - press.current.top,
          ) > DRAG_THRESHOLD_PX &&
          !dragged.current
        ) {
          dragged.current = true;
          clearHoverTimer();
          onDragStart();
        }
      }}
      onPointerLeave={clearHoverTimer}
      sx={{ position: 'relative', pt: showLegendHint ? LEGEND_HINT_SPACE : 0 }}
    >
      {showLegendHint ? (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ position: 'absolute', top: 0, right: 0 }}
        >
          {legendHint}
        </Typography>
      ) : null}
      <LineChartPro
        height={CHART_HEIGHT}
        hideLegend={hideLegend}
        xAxis={[
          {
            id: WEALTH_TREND_X_AXIS_ID,
            scaleType: 'time',
            data: dayDates,
            zoom: {
              minSpan: minZoomSpan(chart.days.length),
              ...(showZoomSlider && { slider: { enabled: true } }),
            },
            domainLimit: X_AXIS_DOMAIN_LIMIT,
            height: X_AXIS_HEIGHT,
            tickMinStep: DAY_MS,
            tickNumber,
            tickInterval: ticksFor(dayDates, zoomData, tickNumber),
            valueFormatter: (value: Date, context) =>
              formatAppDate(value, {
                format:
                  context.location === 'tooltip'
                    ? tooltipDateFormat
                    : APP_MONTH_DAY_FORMAT,
              }),
          },
        ]}
        yAxis={[{ valueFormatter: formatAxisValue, ...yRange }]}
        onHiddenItemsChange={handleHiddenItemsChange}
        series={chart.lines.map((line) => ({
          id: seriesKey(line.id),
          label: line.name,
          data: line.values,
          showMark: !dense,
          connectNulls: true,
          valueFormatter: (value: number | null) =>
            value === null ? '' : formatNumberToCurrency(value, 'USD', 0),
        }))}
        zoomData={zoomData}
        onZoomChange={onZoomChange}
        zoomInteractionConfig={{
          zoom: isTouch ? ['brush', 'pinch'] : ['brush'],
        }}
        onHighlightChange={handleHighlightChange}
        onHighlightedAxisChange={handleHighlightedAxisChange}
        onAxisClick={handleAxisClick}
        onMarkClick={(event, item) => {
          if (!showPopover || dragged.current) return;
          clearHoverTimer();
          pointer.current = { top: event.clientY, left: event.clientX };
          openPoint(seriesIdFromKey(item.seriesId), item.dataIndex);
        }}
        slotProps={{
          legend: {
            toggleVisibilityOnClick: true,
            direction: isCompact ? 'horizontal' : 'vertical',
            position: isCompact
              ? { vertical: 'bottom', horizontal: 'center' }
              : { vertical: 'top', horizontal: 'end' },
          },
          tooltip: { trigger: showPopover ? 'none' : 'axis' },
        }}
      >
        <LineLocatorBridge
          chart={chart}
          locator={locator}
        />
      </LineChartPro>
      {isCompact && !hideLegend ? (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: 'block', textAlign: 'center' }}
        >
          {legendHint}
        </Typography>
      ) : null}
    </Box>
  );
}
